import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, UtensilsCrossed, Leaf, Fish, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Menu = () => {
  const { language, getLocalizedField } = useLanguage();
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [weeklyMenu, setWeeklyMenu] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  // Fallback texts (used when siteContent fields are empty/missing)
  const fallback = {
    fr: { menu_title: 'Notre Carte', menu_subtitle: 'Menu Complet', menu_weekly_title: 'Menu', menu_weekly_midi: 'midi', menu_weekly_end: 'de la Semaine', menu_weekly_subtitle: 'Nos créations de la semaine', menu_reserve_cta: 'Réserver une Table', menu_price_note: 'Les prix sont en euros, service compris.', menu_formulas: 'Nos Formules', menu_formula_full: 'Entrée + Plat + Dessert', menu_formula_ep: 'Entrée + Plat', menu_formula_pd: 'Plat + Dessert', menu_formula_po: 'Plat Seul', menu_dishcat_entree: 'Entrée', menu_dishcat_meat: 'Viande', menu_dishcat_vegetarian: 'Végétarien', menu_dishcat_seafood: 'Poisson / Fruits de Mer', menu_dishcat_dessert: 'Dessert' },
    en: { menu_title: 'Our Menu', menu_subtitle: 'Full Menu', menu_weekly_title: 'Menu', menu_weekly_midi: 'midi', menu_weekly_end: 'of the Week', menu_weekly_subtitle: 'Our creations of the week', menu_reserve_cta: 'Book a Table', menu_price_note: 'Prices are in euros, service included.', menu_formulas: 'Our Formulas', menu_formula_full: 'Starter + Main + Dessert', menu_formula_ep: 'Starter + Main', menu_formula_pd: 'Main + Dessert', menu_formula_po: 'Main Only', menu_dishcat_entree: 'Starter', menu_dishcat_meat: 'Meat', menu_dishcat_vegetarian: 'Vegetarian', menu_dishcat_seafood: 'Fish / Seafood', menu_dishcat_dessert: 'Dessert' },
    pt: { menu_title: 'Nosso Cardápio', menu_subtitle: 'Menu Completo', menu_weekly_title: 'Menu', menu_weekly_midi: 'midi', menu_weekly_end: 'da Semana', menu_weekly_subtitle: 'Nossas criações da semana', menu_reserve_cta: 'Reservar uma Mesa', menu_price_note: 'Preços em euros, serviço incluído.', menu_formulas: 'Nossas Fórmulas', menu_formula_full: 'Entrada + Prato + Sobremesa', menu_formula_ep: 'Entrada + Prato', menu_formula_pd: 'Prato + Sobremesa', menu_formula_po: 'Prato Apenas', menu_dishcat_entree: 'Entrada', menu_dishcat_meat: 'Carne', menu_dishcat_vegetarian: 'Vegetariano', menu_dishcat_seafood: 'Peixe / Frutos do Mar', menu_dishcat_dessert: 'Sobremesa' }
  };

  const get = (field) => {
    if (siteContent) {
      const val = getLocalizedField(siteContent, field);
      if (val) return val;
    }
    return fallback[language]?.[field] || fallback.fr[field] || '';
  };

  const dishCatLabel = (slug) => get(`menu_dishcat_${slug}`);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuItemsRes, categoriesRes, weeklyMenuRes, contentRes] = await Promise.all([
          axios.get(`${API}/menu-items`),
          axios.get(`${API}/menu-categories`),
          axios.get(`${API}/weekly-menu`),
          axios.get(`${API}/content`)
        ]);
        setMenuItems(menuItemsRes.data || []);
        setMenuCategories(categoriesRes.data || []);
        setWeeklyMenu(weeklyMenuRes.data);
        setSiteContent(contentRes.data);
        // Pre-select first category that has available items
        const cats = categoriesRes.data || [];
        const items = menuItemsRes.data || [];
        const firstWithItems = cats.find(c => items.some(i => i.category === c.slug && i.is_available !== false));
        if (firstWithItems) setActiveCategory(firstWithItems.slug);
      } catch (error) {
        console.log('Error fetching data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'entree': return <UtensilsCrossed className="w-5 h-5" />;
      case 'meat': return <UtensilsCrossed className="w-5 h-5" />;
      case 'vegetarian': return <Leaf className="w-5 h-5" />;
      case 'seafood': return <Fish className="w-5 h-5" />;
      default: return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'long' });
  };

  const toggleExpand = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  // Use dynamic categories from database
  const getCategoryName = (category) => {
    const cat = menuCategories.find(c => c.slug === category.slug);
    if (cat) {
      return language === 'fr' ? cat.name_fr : language === 'en' ? cat.name_en : cat.name_pt;
    }
    return category.slug;
  };

  const entrees = weeklyMenu?.dishes?.filter(d => d.category === 'entree') || [];
  const mainDishes = weeklyMenu?.dishes?.filter(d => ['meat', 'vegetarian', 'seafood'].includes(d.category)) || [];
  const desserts = weeklyMenu?.dishes?.filter(d => d.category === 'dessert') || [];

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-20" data-testid="menu-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920&q=80"
            alt="BÉNI Menu"
            className="w-full h-full object-cover opacity-30"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
              {get('menu_subtitle')}
            </p>
            <h1 className="font-display text-5xl md:text-7xl text-white" data-testid="menu-title">
              {get('menu_title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Weekly Menu Section */}
      {loading ? (
        /* Skeleton Loading for Weekly Menu */
        <section className="py-16 px-6 md:px-12 bg-[#121212]" data-testid="weekly-menu-loading">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="h-4 w-48 bg-white/10 rounded mx-auto mb-4 animate-pulse" />
              <div className="h-12 w-96 bg-white/10 rounded mx-auto mb-4 animate-pulse" />
              <div className="h-4 w-64 bg-white/10 rounded mx-auto animate-pulse" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#0a0a0a] border border-white/10 overflow-hidden">
                  <div className="h-56 bg-white/5 animate-pulse" />
                  <div className="p-6">
                    <div className="h-4 w-24 bg-[#d4af37]/20 rounded mb-3 animate-pulse" />
                    <div className="h-6 w-full bg-white/10 rounded mb-2 animate-pulse" />
                    <div className="h-4 w-3/4 bg-white/5 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : weeklyMenu && (entrees.length > 0 || mainDishes.length > 0 || desserts.length > 0) ? (
        <section className="py-16 px-6 md:px-12 bg-[#121212]" data-testid="weekly-menu-section">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
                {get('menu_weekly_subtitle')}
              </p>
              <h2 className="font-display text-4xl md:text-5xl text-white mb-4">
                {get('menu_weekly_title')} <span className="font-accent italic text-[#d4af37]">{get('menu_weekly_midi')}</span> {get('menu_weekly_end')}
              </h2>
              <div className="flex items-center justify-center gap-2 text-white/60">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">
                  {formatDate(weeklyMenu.week_start)} - {formatDate(weeklyMenu.week_end)}
                </span>
              </div>
            </motion.div>

            {/* Entrées */}
            {entrees.length > 0 && (
              <div className="mb-12">
                <div className="flex flex-wrap justify-center gap-8">
                  {entrees.map((dish, index) => (
                  <motion.div
                    key={dish.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="group cursor-pointer w-full max-w-sm"
                    onClick={() => dish.image_url && toggleExpand(`weekly-${dish.id}`)}
                    data-testid={`weekly-dish-${dish.category}`}
                  >
                    <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden hover:border-[#d4af37]/50 transition-all duration-500">
                      {dish.image_url && (
                        <div className="relative h-56 overflow-hidden">
                          <img src={dish.image_url} alt={getLocalizedField(dish, 'name')} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[#d4af37]">{getCategoryIcon(dish.category)}</span>
                          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37]">{dishCatLabel(dish.category)}</p>
                        </div>
                        <h3 className="font-display text-xl text-white mb-2">{getLocalizedField(dish, 'name')}</h3>
                        {getLocalizedField(dish, 'description') && (
                          <p className="text-white/50 text-sm line-clamp-2">{getLocalizedField(dish, 'description')}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Main Dishes */}
            {mainDishes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {mainDishes.map((dish, index) => (
                  <motion.div
                    key={dish.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="group cursor-pointer"
                    onClick={() => dish.image_url && toggleExpand(`weekly-main-${dish.id}`)}
                    data-testid={`weekly-dish-${dish.category}`}
                  >
                    <div className="bg-[#0a0a0a] border border-white/10 overflow-hidden hover:border-[#d4af37]/50 transition-all duration-500">
                      {dish.image_url && (
                        <div className="relative h-56 overflow-hidden">
                          <img src={dish.image_url} alt={getLocalizedField(dish, 'name')} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[#d4af37]">{getCategoryIcon(dish.category)}</span>
                          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37]">{dishCatLabel(dish.category)}</p>
                        </div>
                        <h3 className="font-display text-xl text-white mb-2">{getLocalizedField(dish, 'name')}</h3>
                        {getLocalizedField(dish, 'description') && (
                          <p className="text-white/50 text-sm line-clamp-2">{getLocalizedField(dish, 'description')}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Desserts */}
            {desserts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-3xl mx-auto">
                {desserts.map((dish, index) => (
                  <motion.div
                    key={dish.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="bg-[#0a0a0a] border border-white/10 p-6 hover:border-[#d4af37]/50 transition-all"
                    data-testid={`weekly-dessert-${index}`}
                  >
                    <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">
                      {dishCatLabel('dessert')}
                    </p>
                    <h3 className="font-display text-lg text-white mb-1">
                      {getLocalizedField(dish, 'name')}
                    </h3>
                    {getLocalizedField(dish, 'description') && (
                      <p className="text-white/50 text-sm">
                        {getLocalizedField(dish, 'description')}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}

            {/* Pricing */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-[#0a0a0a] border border-[#d4af37]/30 p-8"
            >
              <h3 className="text-center text-white font-display text-2xl mb-8">{get('menu_formulas')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-[#d4af37]/10 border border-[#d4af37]">
                  <p className="text-[#d4af37] font-display text-3xl mb-1">
                    {weeklyMenu.price_full?.toFixed(2).replace('.', ',')}€
                  </p>
                  <p className="text-white/70 text-xs uppercase tracking-wider">{get('menu_formula_full')}</p>
                </div>
                <div className="text-center p-4 border border-white/10">
                  <p className="text-white font-display text-2xl mb-1">
                    {weeklyMenu.price_entree_plat?.toFixed(2).replace('.', ',')}€
                  </p>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{get('menu_formula_ep')}</p>
                </div>
                <div className="text-center p-4 border border-white/10">
                  <p className="text-white font-display text-2xl mb-1">
                    {weeklyMenu.price_plat_dessert?.toFixed(2).replace('.', ',')}€
                  </p>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{get('menu_formula_pd')}</p>
                </div>
                <div className="text-center p-4 border border-white/10">
                  <p className="text-white font-display text-2xl mb-1">
                    {weeklyMenu.price_plat_only?.toFixed(2).replace('.', ',')}€
                  </p>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{get('menu_formula_po')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ) : null}

      {/* Divider - only show if weekly menu exists or menu items exist */}
      {(weeklyMenu || menuItems.length > 0) && !loading && (
        <div className="py-8 px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="h-px bg-gradient-to-r from-transparent via-[#d4af37]/50 to-transparent" />
          </div>
        </div>
      )}

      {/* Full Menu — Category Tabs + Subcategory Grouping */}
      {loading ? (
        <div className="py-24 text-center">
          <p className="text-white/50">Chargement...</p>
        </div>
      ) : (() => {
        const availableCategories = menuCategories.filter(c =>
          menuItems.some(i => i.category === c.slug && i.is_available !== false)
        );
        if (availableCategories.length === 0) return null;

        const activeItems = menuItems
          .filter(i => i.category === activeCategory && i.is_available !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

        // Group by subcategory (empty subcategory → key "__none__")
        const groups = [];
        const seen = new Map();
        activeItems.forEach(item => {
          const key = (item.subcategory || '').trim() || '__none__';
          if (!seen.has(key)) {
            seen.set(key, groups.length);
            groups.push({ key, label: key === '__none__' ? '' : key, items: [] });
          }
          groups[seen.get(key)].items.push(item);
        });

        return (
          <section className="py-16 px-6 md:px-12" data-testid="full-menu-section">
            <div className="max-w-4xl mx-auto">
              {/* Pill Tabs */}
              <div className="sticky top-20 z-20 -mx-6 md:-mx-12 px-6 md:px-12 py-4 bg-[#0a0a0a]/95 backdrop-blur-sm mb-10 border-b border-white/5">
                <div className="flex flex-wrap justify-center gap-2 md:gap-3" data-testid="menu-category-tabs">
                  {availableCategories.map(cat => {
                    const isActive = cat.slug === activeCategory;
                    const label = language === 'fr' ? cat.name_fr : language === 'en' ? cat.name_en : cat.name_pt;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { setActiveCategory(cat.slug); setExpandedItem(null); }}
                        className={`px-4 md:px-5 py-2 text-xs uppercase tracking-[0.2em] transition-all duration-300 rounded-none border ${
                          isActive
                            ? 'bg-[#d4af37] text-black border-[#d4af37]'
                            : 'bg-transparent text-white/70 border-white/15 hover:border-[#d4af37]/50 hover:text-white'
                        }`}
                        data-testid={`menu-tab-${cat.slug}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Active Category Items */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                >
                  {groups.map((group, gIdx) => (
                    <div key={group.key} className={gIdx > 0 ? 'mt-14' : ''}>
                      {group.label && (
                        <div className="text-center mb-8">
                          <h3 className="font-display text-2xl md:text-3xl text-white inline-block relative pb-2" data-testid={`menu-subcategory-${group.key}`}>
                            {group.label}
                            <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-16 h-px bg-[#d4af37]" />
                          </h3>
                        </div>
                      )}

                      <div className="space-y-4">
                        {group.items.map((item, index) => {
                          const isExpanded = expandedItem === item.id;
                          const hasImage = !!item.image_url;
                          return (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.03 }}
                              className="group"
                              data-testid={`menu-item-${activeCategory}-${index}`}
                            >
                              <div
                                className={`flex justify-between items-center p-4 border border-white/10 transition-all duration-300 ${
                                  hasImage ? 'cursor-pointer hover:border-[#d4af37]/50' : ''
                                } ${isExpanded ? 'border-[#d4af37]/50 bg-white/5' : ''}`}
                                onClick={() => hasImage && toggleExpand(item.id)}
                              >
                                <div className="flex-1 pr-4">
                                  <div className="flex items-center gap-3">
                                    <h3 className="font-display text-xl text-white group-hover:text-[#d4af37] transition-colors">
                                      {getLocalizedField(item, 'name')}
                                    </h3>
                                    {hasImage && (
                                      <ChevronDown className={`w-4 h-4 text-[#d4af37] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                    )}
                                  </div>
                                  {!isExpanded && getLocalizedField(item, 'description') && (
                                    <p className="text-white/50 text-sm mt-1 line-clamp-1">
                                      {getLocalizedField(item, 'description')}
                                    </p>
                                  )}
                                </div>
                                <div className="text-[#d4af37] font-display text-xl whitespace-nowrap">
                                  {typeof item.price === 'number' ? item.price.toFixed(2).replace('.', ',') : item.price}€
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="p-6 bg-[#0a0a0a] border border-t-0 border-[#d4af37]/30">
                                      <div className="flex flex-col md:flex-row gap-6">
                                        {item.image_url && (
                                          <motion.div
                                            className="md:w-1/2 overflow-hidden"
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.5, ease: 'easeOut' }}
                                          >
                                            <motion.img
                                              src={item.image_url}
                                              alt={getLocalizedField(item, 'name')}
                                              className="w-full h-64 object-cover"
                                              loading="lazy"
                                              initial={{ scale: 1.2 }}
                                              animate={{ scale: 1 }}
                                              transition={{ duration: 0.7, ease: 'easeOut' }}
                                            />
                                          </motion.div>
                                        )}
                                        <motion.div
                                          className={item.image_url ? 'md:w-1/2' : 'w-full'}
                                          initial={{ opacity: 0, x: 20 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{ duration: 0.4, delay: 0.2 }}
                                        >
                                          <h4 className="font-display text-2xl text-white mb-4">
                                            {getLocalizedField(item, 'name')}
                                          </h4>
                                          {getLocalizedField(item, 'description') && (
                                            <p className="text-white/70 leading-relaxed">
                                              {getLocalizedField(item, 'description')}
                                            </p>
                                          )}
                                          <p className="text-[#d4af37] font-display text-3xl mt-6">
                                            {typeof item.price === 'number' ? item.price.toFixed(2).replace('.', ',') : item.price}€
                                          </p>
                                        </motion.div>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </section>
        );
      })()}

      {/* Show message if no menu items */}
      {!loading && menuItems.length === 0 && (
        <section className="py-16 px-6 md:px-12 text-center">
          <p className="text-white/50">
            {language === 'fr' ? 'Le menu sera bientôt disponible.' : 
             language === 'en' ? 'Menu coming soon.' : 
             'Menu em breve disponível.'}
          </p>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-20 px-6 md:px-12 text-center">
        <p className="text-white/50 text-sm mb-6">
          {get('menu_price_note')}
        </p>
        <Link
          to="/reservations"
          className="inline-flex items-center gap-3 bg-[#d4af37] text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all duration-300"
          data-testid="menu-reserve-cta"
        >
          {get('menu_reserve_cta')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
};

export default Menu;
